import moment from 'moment'
import { useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { Box, Chart, Header, Stat, StatLabel, StatNumber, dayLabel } from '~/shared/ui'
import { todayIn, useZone, utcToZoned, zonedToUtc } from '~/team_tools/components/timezone'
import { Period, Range, usePeriod, useRange } from '~/team_tools/dashboard/statistic/period'

type GroupBy = 'country' | 'region'

/** What one point on the chart stands for. Day and week points are the
 *  busiest moment of that day or week, worked out by the endpoint. */
type Resolution = 'hour' | 'day' | 'week'

type Concurrency = {
    days: number
    bucket_minutes: number
    resolution?: Resolution
    group_by: GroupBy
    /** Every group online at some sample, largest peak first, Unknown last.
     *  Nothing is merged -- there is no "Other". */
    groups: string[]
    labels: string[]
    series: Record<string, number[]>
    peak: Record<string, number>
    peak_total: number
    sessions: number
    /** In a match at the latest sample. Worked out here, for Live. */
    now_total?: number
}

const DAYS = 7
/** Tiles shown before "Show all". A busy week is seventy countries, which is
 *  a dozen rows of tiles on a desktop and thirty-five on a phone. */
const TOP_TILES = 12
const BUCKET_MINUTES = 60
const DAY_MS = 24 * 60 * 60 * 1000
/** The endpoint looks back at most this far: hourly, and by day or week. */
const MAX_DAYS = 31
const MAX_DAYS_COARSE = 92

/** The span the chosen window covers, and how finely to sample it.
 *
 *  The endpoint answers "N days back from a moment", so the window is asked
 *  for in whole days and trimmed to its exact start afterwards. Live is the
 *  last two hours at the finest resolution, refreshed. No window (outside the
 *  dashboard) is the old last seven days.
 *
 *  Hourly samples every hour for a week or a month (744 points, inside the
 *  endpoint's cap) so the toggle's label is true; Today and Live keep their
 *  ten-minute samples. A daily or weekly chart may look back further, so All
 *  time becomes the longest window the endpoint allows. */
/** Midnight starting `day` ("2026-09-15") in `zone`, as an instant. */
const midnightIn = (day: string, zone: string) => new Date(zonedToUtc(day, '00:00', zone) as string)

/** `day` moved by `n` calendar days, as "YYYY-MM-DD". */
const shiftDay = (day: string, n: number) => {
    const [y, m, d] = day.split('-').map(Number)
    return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

const windowFor = (period: Period | null, range: Range | null, resolution: Resolution, zone: string) => {
    const coarse = resolution !== 'hour'
    const now = new Date()
    // Today, this week and this month start at midnight in the zone the
    // picker names, like every other figure on the Dashboard. They started at
    // the DEVICE's midnight, Monday and 1st, so with the picker on UTC "today"
    // still meant the viewer's own day (DASH-03).
    const today = todayIn(zone).date
    const midnight = midnightIn(today, zone)
    let from: Date
    let to = now
    let bucket = 60
    let label = `last ${DAYS} days`
    switch (period) {
        case 'live':
            from = new Date(now.getTime() - 2 * 60 * 60 * 1000)
            bucket = 10
            label = 'last 2 hours'
            break
        case 'today':
            from = midnight
            bucket = 10
            label = 'today'
            break
        case 'week': {
            const [y, m, d] = today.split('-').map(Number)
            const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
            from = midnightIn(shiftDay(today, -((weekday + 6) % 7)), zone)
            label = 'this week'
            break
        }
        case 'month':
            from = midnightIn(`${today.slice(0, 8)}01`, zone)
            label = 'this month'
            break
        case 'custom':
            if (range) {
                from = midnightIn(range.from, zone)
                to = midnightIn(shiftDay(range.to, 1), zone)
                if (to > now) to = now
                label = `${range.from} to ${range.to}`
                break
            }
            from = new Date(now.getTime() - DAYS * DAY_MS)
            break
        case 'all':
            if (coarse) {
                from = midnightIn(shiftDay(today, -(MAX_DAYS_COARSE - 1)), zone)
                label = `last ${MAX_DAYS_COARSE} days`
                break
            }
            from = new Date(now.getTime() - DAYS * DAY_MS)
            break
        default:
            from = new Date(now.getTime() - DAYS * DAY_MS)
    }
    const limit = coarse ? MAX_DAYS_COARSE : MAX_DAYS
    const span = (to.getTime() - from.getTime()) / DAY_MS
    const days = Math.min(limit, Math.max(1, Math.ceil(span)))
    return {
        from,
        to,
        days,
        span,
        bucket,
        label,
        // The zone is part of the window now: a new zone is a new "today".
        key: `${period}_${range?.from}_${range?.to}_${resolution}_${zone}`,
    }
}

/** The answer cut to the window: only samples from `from` on, with the
 *  peaks worked out again over what is left. Hourly only -- a daily or weekly
 *  answer is asked for from `from` already, and its points are busiest
 *  moments, so peaks recomputed from them would be wrong. */
const trimmed = (data: Concurrency, from: Date): Concurrency => {
    const keep = data.labels.map((label) => new Date(label) >= from)
    const cut = (series: Record<string, number[]>) =>
        Object.fromEntries(Object.entries(series).map(([group, values]) => [group, values.filter((_, i) => keep[i])]))
    const series = cut(data.series)
    const labels = data.labels.filter((_, i) => keep[i])
    const totals = labels.map((_, i) => Object.values(series).reduce((sum, values) => sum + (values[i] ?? 0), 0))
    const peaks = (series: Record<string, number[]>) =>
        Object.fromEntries(Object.entries(series).map(([group, values]) => [group, Math.max(0, ...values)]))
    const peak = peaks(series)
    return {
        ...data,
        labels,
        series,
        peak,
        peak_total: Math.max(0, ...totals),
        groups: data.groups.filter((group) => (peak[group] ?? 0) > 0),
        now_total: totals[totals.length - 1] ?? 0,
    }
}

/** A country code's English name: "AR" is "Argentina". Anything that is not a
 *  two-letter code -- a server region, Unknown -- is returned as it is.
 *  The browser carries the names, so nothing is shipped or kept up to date. */
const regionNames = (() => {
    try {
        return new Intl.DisplayNames(['en'], { type: 'region' })
    } catch {
        return null
    }
})()

const placeName = (group: string) => {
    if (!/^[A-Z]{2}$/.test(group)) return group
    try {
        return regionNames?.of(group) ?? group
    } catch {
        return group
    }
}

const GROUPINGS: [key: GroupBy, label: string, note: string][] = [
    [
        'country',
        'Player country',
        'The country the account signed up from, or, when it has none, where the session was played from. Answers where the audience is.',
    ],
    [
        'region',
        'Server region',
        'Which region’s servers carried the players. Answers where the machines are needed.',
    ],
]

const RESOLUTIONS: [key: Resolution, label: string, note: string][] = [
    ['hour', 'Hourly', 'Players in a match at each moment.'],
    ['day', 'Daily', 'The busiest moment of each day, split by group at that moment.'],
    ['week', 'Weekly', 'The busiest moment of each week (Monday first), split by group at that moment.'],
]

/** The window each resolution needs to draw at least two points. */
const MIN_DAYS: Record<Resolution, number> = { hour: 0, day: 2, week: 14 }

// The first twelve are hand-picked and in a fixed order, so the biggest lines
// keep their colour between refreshes. Past them -- there is no cap on groups,
// and a busy week is seventy countries -- each further hue steps round the wheel
// by the golden angle, which keeps neighbours far apart however many there are,
// alternating lightness so two near hues still part. Unknown is greyed: it is a
// data-quality figure, not a place, and should not pull the eye from one.
//
// Hex rather than rgb() so the fill can be derived by appending an alpha pair.
const COLOURS = [
    '#22c55e', '#eab308', '#9945FF', '#f43f5e', '#38bdf8', '#fb923c',
    '#a3e635', '#e879f9', '#2dd4bf', '#f87171', '#c084fc', '#facc15',
]
const MUTED: Record<string, string> = { Unknown: '#64748b' }

const hslHex = (h: number, s: number, l: number) => {
    const a = s * Math.min(l, 1 - l)
    const f = (n: number) => {
        const k = (n + h / 30) % 12
        const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
        return Math.round(c * 255).toString(16).padStart(2, '0')
    }
    return `#${f(0)}${f(8)}${f(4)}`
}

const colourFor = (group: string, index: number) =>
    MUTED[group] ??
    COLOURS[index] ??
    hslHex((index * 137.508) % 360, 0.65, index % 2 ? 0.5 : 0.65)

// Opaque enough to read as a band, sheer enough that the grid stays visible
// through the stack.
const FILL_ALPHA = 'CC'

function Segmented<T extends string>({
    options,
    value,
    onChange,
    disabled,
}: {
    options: [key: T, label: string, note: string][]
    value: T
    onChange: (next: T) => void
    disabled?: (key: T) => string | undefined
}) {
    return (
        <div className='flex flex-row gap-2 justify-center pt-1'>
            {options.map(([key, label, note]) => {
                const why = disabled?.(key)
                return (
                    <button
                        key={key}
                        type='button'
                        title={why ?? note}
                        disabled={Boolean(why)}
                        onClick={() => onChange(key)}
                        className={[
                            'text-xs px-3 py-1 rounded border transition-colors',
                            why
                                ? 'border-white/10 text-white/20 cursor-not-allowed'
                                : value === key
                                  ? 'border-violet-500 text-violet-400 bg-violet-500/10'
                                  : 'border-white/20 text-white/50 hover:border-white/40 hover:text-white/80',
                        ].join(' ')}
                    >
                        {label}
                    </button>
                )
            })}
        </div>
    )
}

export const ConcurrentPlayers = () => {
    const [groupBy, setGroupBy] = useState<GroupBy>('country')
    const [chosen, setChosen] = useState<Resolution>('hour')
    const [showAll, setShowAll] = useState(false)
    const [data, setData] = useState<Concurrency>()
    // The endpoint is Marketing-only and the Dashboard is open to all staff. A
    // refusal used to leave the peak tile pulsing forever with no message.
    const [failed, setFailed] = useState<string>()
    const period = usePeriod()
    const range = useRange()
    const [zone] = useZone()
    const live = period === 'live'

    // Live and Today are hours long, so only Hourly means anything there. A
    // choice the window cannot draw falls back to Hourly rather than to an
    // empty chart, and comes back by itself when the window grows again.
    const hourly = live || period === 'today'
    const coarseSpan = windowFor(period, range, 'day', zone).span
    const tooShort = (key: Resolution) => {
        const minDays = MIN_DAYS[key]
        return coarseSpan < minDays ? `Needs a window of at least ${minDays} days.` : undefined
    }
    const resolution: Resolution = hourly || tooShort(chosen) ? 'hour' : chosen
    const coarse = resolution !== 'hour'
    const window = windowFor(period, range, resolution, zone)

    // Live asks again every minute; nothing else needs to.
    const [tick, setTick] = useState(0)
    useEffect(() => {
        if (!live) return
        const timer = setInterval(() => setTick((n) => n + 1), 60 * 1000)
        return () => clearInterval(timer)
    }, [live])

    useEffect(() => {
        // Cleared on the way out so switching axis shows the loading state
        // rather than the previous grouping's lines under the new legend. A
        // Live refresh keeps the old numbers up until the new ones land.
        if (!tick || !live) setData(undefined)
        setFailed(undefined)
        let current = true
        jsonrpc('gameapi.statistic.concurrency', {
            days: period ? window.days : DAYS,
            bucket_minutes: period ? window.bucket : BUCKET_MINUTES,
            group_by: groupBy,
            start_date: moment(window.to).toISOString(true),
            live,
            // Days and weeks are cut in the dashboard's zone, and start where
            // the window does, so the answer needs no trimming here.
            ...(coarse ? { resolution, tz: zone, since: moment(window.from).toISOString(true) } : {}),
        })
            .then((res) => {
                if (!current) return
                setData(period && res.result && !coarse ? trimmed(res.result, window.from) : res.result)
            })
            .catch((err) => {
                if (!current) return
                setFailed(
                    err?.error?.code === 403
                        ? 'Your role cannot see concurrent players.'
                        : 'Concurrent players could not be loaded.'
                )
            })
        return () => {
            current = false
        }
    }, [groupBy, window.key, tick, coarse ? zone : ''])

    const groups = data?.groups ?? []
    // Collapsed, not merged: every country is still its own band on the chart
    // and one click away here. Unknown stays visible either way -- it is the
    // data-quality figure, and hiding it would hide a problem.
    const places = groups.filter((group) => group !== 'Unknown')
    const hidden = Math.max(0, places.length - TOP_TILES)
    const tiles = showAll || !hidden ? groups : [...places.slice(0, TOP_TILES), ...groups.filter((g) => g === 'Unknown')]
    const country = groupBy === 'country'
    const nameOf = (group: string) => (country ? placeName(group) : group)
    // Day and week keys are local midnights in the dashboard's zone; drawn as
    // instants they would land on the evening before for anybody west of it.
    // Hourly labels are instants, and an instant is drawn at the device's own
    // time; written as the picked zone's wall-clock time with no offset they
    // are drawn at that time instead, the trick `dayLabel` uses for days.
    const labels = (data?.labels ?? []).map((label) => {
        if (coarse) return dayLabel(label)
        const at = utcToZoned(label, zone)
        return at.date ? `${at.date}T${at.time}:00` : label
    })

    return (
        <Box className='w-full text-center gap-2'>
            <Header>Concurrent Players</Header>
            <Segmented<GroupBy> options={GROUPINGS} value={groupBy} onChange={setGroupBy} />
            {!hourly && (
                <Segmented<Resolution> options={RESOLUTIONS} value={resolution} onChange={setChosen} disabled={tooShort} />
            )}
            {failed && <small className='text-red-300'>{failed}</small>}

            <Box className='w-full gap-2 pt-2'>
                <Stat.Gray className='w-full' loading={data === undefined && !failed}>
                    <StatLabel className='uppercase tracking-widest text-amber-500'>
                        {live ? 'In a match right now' : `Peak at once — ${window.label}`}
                    </StatLabel>
                    <StatNumber className='text-3xl sm:text-4xl md:text-5xl'>
                        {live ? data?.now_total : data?.peak_total}
                    </StatNumber>
                    <StatLabel className='text-white/40'>
                        {live && data
                            ? `peak ${data.peak_total} in the last 2 hours, refreshes every minute`
                            : data
                              ? `across ${data.sessions.toLocaleString('en-US')} sessions`
                              : undefined}
                    </StatLabel>
                </Stat.Gray>
            </Box>

            {/* Per-group peaks. These deliberately do not add up to the total
                above: groups peak at different hours, so summing them would
                report a concurrency that never happened. */}
            <Box className='w-full gap-2 pt-2'>
                <small className='text-white/50'>
                    Each group’s own peak. They do not sum to the figure above — different places peak at different
                    hours.
                </small>
                {/* Every group has a tile, so this grid is also the chart's legend --
                    a legend of seventy entries under the plot was unreadable.
                    Colours are indexed by the full ranking, so a tile keeps its
                    colour whether the list is collapsed or not. */}
                <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 w-full'>
                    {tiles.map((group) => (
                        <Stat.Gray key={group} loading={data === undefined}>
                            <StatLabel style={{ color: colourFor(group, groups.indexOf(group)) }} title={group}>
                                {nameOf(group)}
                                {country && nameOf(group) !== group && (
                                    <span className='text-white/30'> · {group}</span>
                                )}
                            </StatLabel>
                            <StatNumber>{data?.peak[group]}</StatNumber>
                        </Stat.Gray>
                    ))}
                    {data && groups.length === 0 && (
                        <Stat.Gray className='col-start-1 col-end-[-1]'>
                            <StatLabel className='text-white/40'>nobody played in this window</StatLabel>
                        </Stat.Gray>
                    )}
                </div>
                {hidden > 0 && (
                    <button
                        type='button'
                        onClick={() => setShowAll((open) => !open)}
                        className='text-xs px-3 py-1 rounded border border-white/20 text-white/60 hover:border-white/40 hover:text-white/90 transition-colors'
                    >
                        {showAll ? `Show top ${TOP_TILES}` : `Show all ${places.length} ${country ? 'countries' : 'groups'}`}
                    </button>
                )}
            </Box>

            <Box className='w-full gap-2 pt-4'>
                <Box.Gray className='w-full'>
                    <Chart.TimeSeries
                        className='h-56 sm:h-72'
                        data={{
                            labels,
                            // Stacked, so the top of the stack is total concurrency
                            // and the headline figure is somewhere the eye can find
                            // it. Drawn as unstacked lines, the tallest thing on the
                            // chart was Unknown — a number that is not a place —
                            // while the total it belonged to appeared nowhere, and
                            // the axis it set squashed every real country into the
                            // bottom tenth of the plot.
                            datasets: groups.map((group, i) => ({
                                label: group,
                                data: data?.series[group] ?? [],
                                borderColor: colourFor(group, i),
                                backgroundColor: colourFor(group, i) + FILL_ALPHA,
                                borderWidth: 1,
                                fill: true,
                            })),
                        }}
                        options={{
                            plugins: {
                                tooltip: {
                                    // Restated: this object replaces the shared
                                    // tooltip options rather than merging into them.
                                    mode: 'index' as const,
                                    // Thirteen rows of "UA: 0" buried the four that
                                    // had anybody in them.
                                    filter: (item: any) => item.parsed.y > 0,
                                    callbacks: {
                                        label: (item: any) => `${nameOf(item.dataset.label)}: ${item.parsed.y}`,
                                    },
                                },
                                // The tiles above are the legend; a bottom legend
                                // of every country only pushed the plot off screen.
                                // Region grouping is three entries and keeps it.
                                legend: { display: !country, position: 'bottom' as const, labels: { boxWidth: 12, boxHeight: 12, padding: 12, usePointStyle: true } },
                            },
                            scales: {
                                // The x axis is re-declared rather than inherited:
                                // Chart.TimeSeries merges `scales` one level deep, so
                                // naming `y` alone would keep x but naming both means
                                // this one has to carry its own type.
                                x: {
                                    type: 'time' as const,
                                    // `unit: 'day'` is not cosmetic. Left to choose, Chart.js
                                    // picks 'hour' for an hourly series, and the hour format
                                    // ('9AM') carries no date — so seven daily peaks rendered
                                    // as ticks reading 9AM, 10AM, 11AM and a week of history
                                    // looked like one morning.
                                    time: {
                                        unit: hourly ? ('hour' as const) : resolution === 'week' ? ('week' as const) : ('day' as const),
                                        tooltipFormat:
                                            resolution === 'week'
                                                ? '[Busiest moment, week of] MMM D'
                                                : resolution === 'day'
                                                  ? '[Busiest moment,] ddd MMM D'
                                                  : 'MMM D, HH:mm',
                                    },
                                    ticks: {
                                        autoSkip: true,
                                        maxTicksLimit: 7,
                                        maxRotation: 0,
                                        font: { size: 10 },
                                    },
                                    grid: { display: false },
                                },
                                y: {
                                    min: 0,
                                    // The bands are cumulative; without this Chart.js
                                    // draws them overlapping and the fills hide each
                                    // other rather than adding up.
                                    stacked: true,
                                    // Players are whole; a "2.5 concurrent" tick is nonsense.
                                    ticks: { precision: 0, maxTicksLimit: 5, font: { size: 10 } },
                                    grid: { color: 'rgba(255,255,255,0.06)' },
                                },
                            },
                            elements: { point: { radius: coarse ? 2 : 0, hitRadius: 12 } },
                        }}
                    />
                </Box.Gray>
                <small className='text-white/40'>
                    {coarse &&
                        `Each point is the busiest moment of its ${resolution}, split by ${country ? 'country' : 'region'} at that moment. `}
                    {country
                        ? 'The country the account signed up from. Accounts with none (most game-client-only players) are placed by the address the session was played from. Unknown means neither was available.'
                        : 'Only three server regions exist: South American servers report as US, because the lobby never had a fourth.'}
                </small>
            </Box>
        </Box>
    )
}
